import { useCallback, useMemo, useState } from "react"
import { useLocalization } from "@tm/localization"
import {
    BasketButton,
    Button,
    buttonClasses,
    filledInputClasses,
    Icon,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Stack,
    styled,
    Typography,
} from "@tm/components"
import { CompilationModel } from "@tm/data/compilations"

type ItemSelectionProps = {
    allSelected: boolean
    selectedItemIds: string[]
    lists: { id: string; name: string }[]
    selectedListId?: string
    positionType: "article" | "repairtime"
    onSelectAll(): void
    onUnselectAll(): void
    onAddToBasket?(ids: string[]): Promise<void>
    onMove?: (ids: string[], sourceId: string | undefined, targetId: string) => Promise<void>
    onDelete?: (ids: string[]) => Promise<void>
}

const SelectionPaper = styled(Paper)(() => ({
    position: "absolute",
    zIndex: 5,
    top: -16,
    right: 16,
    padding: "0 0 0 12px",
    boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.54)",
    border: "1px solid #82c4f8",
    borderRadius: "3px",
}))

const StyledSelect = styled(Select)(() => ({
    [`.${filledInputClasses.input}`]: {
        padding: "5px 28px 5px 13px !important",
        borderRadius: "3px",
    },
}))

const LightTypography = styled(Typography)(() => ({
    color: "#202020",
    opacity: "0.54",
}))

const CancelButton = styled(IconButton)(({ theme }) => ({
    [`&.${buttonClasses.contained}`]: {
        height: "42px",
        width: "56px",
        backgroundColor: theme.palette.grey[800],
        color: "#fff",
        marginLeft: "8px",
        borderBottomLeftRadius: "0 !important",
        borderTopLeftRadius: "0 !important",
    },
}))

export function ItemSelection(props: ItemSelectionProps) {
    const { selectedListId, allSelected, selectedItemIds, onSelectAll, onUnselectAll, onMove, onDelete, lists, positionType, onAddToBasket } = props
    const { translateText } = useLocalization()
    const [isLoading, setIsLoading] = useState(false)

    function handleSelectButtonClick() {
        if (allSelected) {
            onUnselectAll()
        } else {
            onSelectAll()
        }
    }

    function handleCancelButtonClick() {
        onUnselectAll()
    }

    const handleDelete = useCallback(async () => {
        try {
            setIsLoading(true)
            await onDelete?.(selectedItemIds)
        } finally {
            setIsLoading(false)
        }
    }, [selectedItemIds])

    const handleMoveDropdownChange = useCallback(
        async (id: string) => {
            if (id !== selectedListId) {
                try {
                    setIsLoading(true)
                    await onMove?.(selectedItemIds, selectedListId, id)
                } finally {
                    setIsLoading(false)
                }
            }
        },
        [selectedItemIds, selectedListId]
    )

    const handleAddToBasket = useCallback(async () => {
        try {
            setIsLoading(true)
            await onAddToBasket?.(selectedItemIds)
        } finally {
            setIsLoading(false)
        }
    }, [selectedItemIds])

    const basketButtonTitle = useMemo(() => {
        switch (positionType) {
            case "repairtime":
                return translateText(916)
            case "article":
            default:
                return translateText(937)
        }
    }, [])

    if (!selectedItemIds.length) {
        return null
    }

    return (
        <SelectionPaper>
            <Stack direction="row" gap={1} alignItems="center">
                {/* Texts */}
                <LightTypography variant="body2">{`${translateText(701).toUpperCase()} `}</LightTypography>
                <Typography variant="body2">
                    {selectedItemIds.length} {translateText(479)}
                </Typography>

                {/* Select Button */}
                <Button onClick={handleSelectButtonClick} disabled={isLoading}>
                    {translateText(allSelected ? 703 : 702)}
                </Button>

                {/* Move Select */}
                {lists && onMove && (
                    <StyledSelect
                        renderValue={(val) => (
                            <Stack direction="row" gap={1} alignItems="center">
                                <Icon name="move" />
                                {(val as CompilationModel).name}
                            </Stack>
                        )}
                        onChange={(e) => handleMoveDropdownChange(e.target.value as string)}
                        disabled={lists.length < (!selectedListId ? 1 : 2) || isLoading}
                        defaultValue={{ name: translateText(13961) }}
                        MenuProps={{
                            anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                            },
                            sx: {
                                maxHeight: "400px",
                            },
                        }}
                    >
                        {lists
                            .filter((list) => list.id !== selectedListId)
                            .map((list) => (
                                <MenuItem key={list.id} value={list.id}>
                                    {list.name}
                                </MenuItem>
                            ))}
                    </StyledSelect>
                )}

                {onAddToBasket && <BasketButton title={basketButtonTitle} onClick={handleAddToBasket} disabled={isLoading} sx={{ width: "70px" }} />}

                {/* Action Buttons */}
                {onDelete && (
                    <IconButton onClick={handleDelete} variant="contained" size="large" disabled={isLoading}>
                        <Icon name="delete" />
                    </IconButton>
                )}
                <CancelButton title={translateText(317)} onClick={handleCancelButtonClick} variant="contained" size="large" disabled={isLoading}>
                    <Icon name="cancel-x" />
                </CancelButton>
            </Stack>
        </SelectionPaper>
    )
}
